/**
 * pdp-real-results.js
 * Active-center carousel for the "Real Results in Action" section.
 *
 * Behaviour:
 *  - The currently-active card is ALWAYS the center (featured / --lg) one.
 *  - Prev/Next shifts the active card one position; the old active shrinks
 *    to --sm and the new active grows to --lg, while the track slides so
 *    the new active sits centered in the viewport.
 *  - Videos autoplay muted on all cards. When a card becomes the active
 *    center card its video is unmuted automatically; when it leaves center
 *    it is muted again. The mute button on the active card lets the user
 *    manually toggle sound.
 *  - Infinite loop: the original block list is cloned N times on each side
 *    (SETS_PER_SIDE). Once a transition ends in a clone region, the index
 *    silently snaps back into the middle set (transitions disabled for the
 *    jump), so users never see an empty side.
 *
 * No dependencies.
 */
(function () {
  'use strict';

  var GAP = 24;                       // must match CSS gap on the track
  var SETS_PER_SIDE = 2;              // 2 prefix + 1 middle + 2 suffix = 5 sets total
  var SWIPE_THRESHOLD = 40;           // px
  var NO_TRANS_CLASS = 'pdp-real-results__track--no-transition';

  function cloneCard(card) {
    var clone = card.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-carousel-clone', '');
    // Strip any inner IDs (avoid duplicate-id validation warnings)
    clone.querySelectorAll('[id]').forEach(function (el) {
      el.removeAttribute('id');
    });
    // Clones shouldn't grab keyboard focus (the real card already does)
    clone.querySelectorAll('a, button').forEach(function (el) {
      el.setAttribute('tabindex', '-1');
    });
    // Ensure cloned videos start muted and restart from the beginning
    clone.querySelectorAll('video[data-video]').forEach(function (v) {
      v.muted = true;
      v.currentTime = 0;
    });
    return clone;
  }

  // ── Video helpers ────────────────────────────────────────────────────────

  /**
   * Update the mute-button icons and aria-label to reflect the current
   * muted state of the video.
   */
  function updateMuteIcon(btn, isMuted) {
    if (!btn) return;
    var iconOn  = btn.querySelector('.pdp-real-results__icon-sound-on');
    var iconOff = btn.querySelector('.pdp-real-results__icon-muted');
    if (iconOn)  iconOn.style.display  = isMuted ? 'none' : '';
    if (iconOff) iconOff.style.display = isMuted ? '' : 'none';
    btn.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
  }

  /**
   * Mute a card's video and reset its mute-button icon.
   */
  function muteCard(cardWrapper) {
    var video   = cardWrapper.querySelector('video[data-video]');
    var muteBtn = cardWrapper.querySelector('[data-mute-btn]');
    if (!video) return;
    video.muted = true;
    updateMuteIcon(muteBtn, true);
  }

  /**
   * Unmute a card's video (only the real active card — never a clone).
   * Falls back to muted if the browser's autoplay policy blocks it.
   */
  function unmuteCard(cardWrapper) {
    var video   = cardWrapper.querySelector('video[data-video]');
    var muteBtn = cardWrapper.querySelector('[data-mute-btn]');
    if (!video) return;

    video.muted = false;
    updateMuteIcon(muteBtn, false);

    var p = video.play();
    if (p !== undefined) {
      p.catch(function () {
        // Browser blocked unmuted autoplay (no prior user gesture yet).
        // Revert to muted so the video keeps playing silently.
        video.muted = true;
        updateMuteIcon(muteBtn, true);
      });
    }
  }

  // ── Carousel init ────────────────────────────────────────────────────────

  function initCarousel(sectionEl) {
    var track   = sectionEl.querySelector('[id^="pdp-real-results-track-"]');
    var btnPrev = sectionEl.querySelector('[id^="pdp-real-results-prev-"]');
    var btnNext = sectionEl.querySelector('[id^="pdp-real-results-next-"]');

    if (!track || !btnPrev || !btnNext) return;

    var wrap = track.parentElement;

    var originalCards = Array.prototype.slice.call(
      track.querySelectorAll(':scope > .pdp-real-results__card-wrapper')
    );
    var N = originalCards.length;
    if (N < 1) return;

    // Determine starting active card: whichever block was flagged featured
    // in Liquid, else the middle block.
    var initialFeaturedIndex = -1;
    for (var i = 0; i < N; i++) {
      if (originalCards[i].classList.contains('pdp-real-results__card-wrapper--featured')) {
        initialFeaturedIndex = i;
        break;
      }
    }
    if (initialFeaturedIndex < 0) initialFeaturedIndex = Math.floor(N / 2);

    // Wipe any server-rendered featured state; JS owns this from here on.
    originalCards.forEach(function (c) {
      c.classList.remove('pdp-real-results__card-wrapper--featured');
      var inner = c.querySelector('.pdp-real-results__card');
      if (inner) {
        inner.classList.remove('pdp-real-results__card--lg');
        inner.classList.add('pdp-real-results__card--sm');
      }
      // Ensure all videos start muted
      muteCard(c);
    });

    // Clone sets on each side of the original list.
    var prefixFrag = document.createDocumentFragment();
    var suffixFrag = document.createDocumentFragment();
    for (var s = 0; s < SETS_PER_SIDE; s++) {
      originalCards.forEach(function (c) {
        prefixFrag.appendChild(cloneCard(c));
        suffixFrag.appendChild(cloneCard(c));
      });
    }
    track.insertBefore(prefixFrag, track.firstChild);
    track.appendChild(suffixFrag);

    var allCards = Array.prototype.slice.call(
      track.querySelectorAll(':scope > .pdp-real-results__card-wrapper')
    );

    // Middle-set index range (these map 1:1 with the original blocks).
    var MIDDLE_START = SETS_PER_SIDE * N;              // inclusive
    var MIDDLE_END   = (SETS_PER_SIDE + 1) * N;        // exclusive

    var currentIndex = MIDDLE_START + initialFeaturedIndex;

    // Infinite loop => buttons never hit a boundary.
    btnPrev.disabled = false;
    btnNext.disabled = false;

    var pendingRAF = null;

    function applyFeatured(index) {
      for (var i = 0; i < allCards.length; i++) {
        var c     = allCards[i];
        var inner = c.querySelector('.pdp-real-results__card');
        var isActive = (i === index);

        c.classList.toggle('pdp-real-results__card-wrapper--featured', isActive);
        if (inner) {
          inner.classList.toggle('pdp-real-results__card--lg', isActive);
          inner.classList.toggle('pdp-real-results__card--sm', !isActive);
        }

        // // Video sound: only the real active card (never a clone) gets unmuted.
        // var isClone = c.hasAttribute('data-carousel-clone');
        // if (isActive && !isClone) {
        //   unmuteCard(c);
        // } else {
        //   muteCard(c);
        // }
      }
    }

    function centerActive() {
      // offsetWidth forces layout, so we read the post-class-change widths.
      var wrapWidth = wrap.offsetWidth;
      var offset = 0;
      for (var i = 0; i < currentIndex; i++) {
        offset += allCards[i].offsetWidth + GAP;
      }
      offset += allCards[currentIndex].offsetWidth / 2;

      var translateX = (wrapWidth / 2) - offset;
      track.style.transform = 'translateX(' + translateX + 'px)';
    }

    function update(withTransition) {
      // Safety net: if somebody slammed next/prev faster than we could snap,
      // wrap the index back into range and do it instantly.
      if (currentIndex < 0 || currentIndex >= allCards.length) {
        while (currentIndex < 0) currentIndex += N;
        while (currentIndex >= allCards.length) currentIndex -= N;
        withTransition = false;
      }

      // Cancel any in-flight no-transition cleanup so it doesn't land mid-update.
      if (pendingRAF !== null) {
        cancelAnimationFrame(pendingRAF);
        pendingRAF = null;
        track.classList.remove(NO_TRANS_CLASS);
      }

      if (!withTransition) {
        track.classList.add(NO_TRANS_CLASS);
      }

      applyFeatured(currentIndex);
      centerActive();

      if (!withTransition) {
        // Commit the no-transition state + new transform/classes in one paint…
        void track.offsetHeight;
        // …then re-enable transitions on the next frame so future updates animate.
        pendingRAF = requestAnimationFrame(function () {
          track.classList.remove(NO_TRANS_CLASS);
          pendingRAF = null;
        });
      }
    }

    function snapBackIfNeeded() {
      var before = currentIndex;
      while (currentIndex < MIDDLE_START) currentIndex += N;
      while (currentIndex >= MIDDLE_END)  currentIndex -= N;
      if (currentIndex !== before) {
        update(false);
      }
    }

    // When the slide transition finishes, re-anchor to the middle set if
    // we've wandered into a clone region.
    track.addEventListener('transitionend', function (e) {
      if (e.target !== track) return;
      if (e.propertyName !== 'transform') return;
      snapBackIfNeeded();
    });

    btnPrev.addEventListener('click', function () {
      currentIndex -= 1;
      update(true);
    });
    btnNext.addEventListener('click', function () {
      currentIndex += 1;
      update(true);
    });

    // ── Mute button — delegated on the track so it works even after
    //    the DOM is mutated (clones re-added on section reload).
    track.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-mute-btn]');
      if (!btn) return;
      // Ignore clicks on clone cards — they're aria-hidden visual duplicates.
      var wrapper = btn.closest('.pdp-real-results__card-wrapper');
      if (!wrapper || wrapper.hasAttribute('data-carousel-clone')) return;

      var video = wrapper.querySelector('video[data-video]');
      if (!video) return;

      video.muted = !video.muted;
      updateMuteIcon(btn, video.muted);

      // If user unmuted manually, make sure the video is actually playing.
      if (!video.muted) {
        video.play().catch(function () {
          video.muted = true;
          updateMuteIcon(btn, true);
        });
      }
    });

    // Touch swipe — useful on mobile/tablet where Prev/Next is less comfortable.
    var touchStartX = null;
    wrap.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { touchStartX = null; return; }
      touchStartX = e.touches[0].clientX;
    }, { passive: true });
    wrap.addEventListener('touchend', function (e) {
      if (touchStartX === null) return;
      var dx = e.changedTouches[0].clientX - touchStartX;
      touchStartX = null;
      if (Math.abs(dx) < SWIPE_THRESHOLD) return;
      currentIndex += (dx < 0) ? 1 : -1;
      update(true);
    }, { passive: true });

    // Recompute on resize — card widths / viewport center move.
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        update(false);
      }, 150);
    });

    // Initial placement — double rAF lets layout + webfonts settle first.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        update(false);
      });
    });
  }

  function init() {
    document.querySelectorAll('.pdp-real-results').forEach(function (sectionEl) {
      if (sectionEl.dataset.carouselInitialized === 'true') return;
      sectionEl.dataset.carouselInitialized = 'true';
      initCarousel(sectionEl);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme-editor live reload
  document.addEventListener('shopify:section:load', function (e) {
    var sectionEl = e.target.querySelector('.pdp-real-results');
    if (!sectionEl) return;
    delete sectionEl.dataset.carouselInitialized;
    initCarousel(sectionEl);
    sectionEl.dataset.carouselInitialized = 'true';
  });
})();